import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import { PROJECT_MEMBER_SEARCH_PER_PAGE } from '../lib/projectMember.constants'
import {
  TaskTrackerCatalogUser,
  TaskTrackerMemberSearchUser,
  TaskTrackerTrackerUserRecord,
} from '../lib/taskTrackerUser.types'
import {
  TaskTrackerSearchUsersMeta,
  TaskTrackerSearchUsersResult,
} from '../lib/projectMember.types'
import {
  mapCatalogUserFromApi,
  mapLmsSearchUserFromApi,
  mapTrackerUserRecordFromApi,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  ApiCatalogUsersPaginated,
  ApiSearchUsersPaginated,
  ApiTrackerUserMutationResponse,
} from './taskTracker.api.types'

const buildSearchQuery = (
  search: string,
  page: number,
  perPage: number,
): Record<string, string | number> => {
  const trimmedSearch = search.trim()
  const query: Record<string, string | number> = {
    page,
    per_page: perPage,
  }

  if (trimmedSearch) {
    query.search = trimmedSearch
  }

  return query
}

const mapPaginatedMeta = (
  meta: ApiCatalogUsersPaginated['_meta'] | undefined,
  page: number,
  perPage: number,
): TaskTrackerSearchUsersMeta => ({
  totalCount: Number(meta?.totalCount ?? 0),
  pageCount: Number(meta?.pageCount ?? 0),
  currentPage: Number(meta?.currentPage ?? page),
  perPage: Number(meta?.perPage ?? perPage),
  maxCount:
    meta?.maxCount == null
      ? null
      : Number.isFinite(Number(meta.maxCount))
        ? Number(meta.maxCount)
        : null,
})

const mapCatalogUsersResult = (
  payload: ApiCatalogUsersPaginated | undefined,
  page: number,
  perPage: number,
): TaskTrackerSearchUsersResult<TaskTrackerCatalogUser> => ({
  items: (payload?.data ?? []).map(mapCatalogUserFromApi),
  meta: mapPaginatedMeta(payload?._meta, page, perPage),
})

const mapLmsUsersResult = (
  payload: ApiSearchUsersPaginated | undefined,
  page: number,
  perPage: number,
): TaskTrackerSearchUsersResult<TaskTrackerMemberSearchUser> => ({
  items: (payload?.data ?? []).map(mapLmsSearchUserFromApi),
  meta: mapPaginatedMeta(payload?._meta, page, perPage),
})

export const taskTrackerUsersApi = {
  async getCatalogUsers(
    search = '',
    page = 1,
    perPage = PROJECT_MEMBER_SEARCH_PER_PAGE,
  ): Promise<TaskTrackerSearchUsersResult<TaskTrackerCatalogUser>> {
    const res = await API.get<ResponseWrapper<ApiCatalogUsersPaginated>>(
      `task-tracker-users/index${getParams(buildSearchQuery(search, page, perPage))}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить каталог пользователей'),
      )
    }

    return mapCatalogUsersResult(res.data?.data, page, perPage)
  },

  async searchLmsUsers(
    search = '',
    page = 1,
    perPage = PROJECT_MEMBER_SEARCH_PER_PAGE,
  ): Promise<TaskTrackerSearchUsersResult<TaskTrackerMemberSearchUser>> {
    const res = await API.get<ResponseWrapper<ApiSearchUsersPaginated>>(
      `task-tracker-users/search-users${getParams(buildSearchQuery(search, page, perPage))}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось выполнить поиск сотрудников'),
      )
    }

    return mapLmsUsersResult(res.data?.data, page, perPage)
  },

  async createTrackerUser(
    userId: number,
  ): Promise<TaskTrackerTrackerUserRecord> {
    const res = await API.post<ResponseWrapper<ApiTrackerUserMutationResponse>>(
      'task-tracker-users/create',
      {
        user_id: userId,
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось добавить пользователя в каталог'),
      )
    }
    if (!res.data?.data?.tracker_user) {
      throw new Error('Не удалось добавить пользователя в каталог')
    }

    return mapTrackerUserRecordFromApi(res.data.data.tracker_user)
  },

  async updateTrackerUser(
    trackerUserId: number,
    payload: { userId?: number },
  ): Promise<TaskTrackerTrackerUserRecord> {
    const body: Record<string, number> = {}
    if (payload.userId != null) body.user_id = payload.userId

    const res = await API.patch<ResponseWrapper<ApiTrackerUserMutationResponse>>(
      `task-tracker-users/update${getParams({ task_tracker_user_id: trackerUserId })}`,
      body,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось обновить пользователя каталога'),
      )
    }
    if (!res.data?.data?.tracker_user) {
      throw new Error('Не удалось обновить пользователя каталога')
    }

    return mapTrackerUserRecordFromApi(res.data.data.tracker_user)
  },

  async deleteTrackerUser(trackerUserId: number): Promise<{
    deleted: boolean
    trackerUserId: number
    userId: number
  }> {
    const res = await API.delete<
      ResponseWrapper<{
        deleted: boolean
        tracker_user_id: number
        user_id: number
      }>
    >(
      `task-tracker-users/delete${getParams({ task_tracker_user_id: trackerUserId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось удалить пользователя из каталога'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось удалить пользователя из каталога')
    }

    return {
      deleted: Boolean(res.data.data.deleted),
      trackerUserId: Number(res.data.data.tracker_user_id),
      userId: Number(res.data.data.user_id),
    }
  },
}
